import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Text,
  TextInput,
  Heading,
  Spinner,
  DropButton,
} from "grommet";
import { FormDown, FormUp, Refresh, View } from "grommet-icons";
import axios from "axios";
import API_URL from "../../config/config";
import { useNavigate, useParams } from "react-router-dom";
import { HeaderNavigation } from "../../components/Headers/GlobalHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import BreadcrumbHeader from "../../components/ManagerPortal/BreadcrumbHeader";
import AssessmentList from "../../components/ManagerPortal/AssessmentList";
import moment from "moment-timezone";

const ManagerAssessmentArchive = () => {
  const { manager_id } = useParams();
  const [assessmentsData, setAssessmentsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAssessment, setSelectedAssessment] = useState(null); // Store the selected assessment
  const [showModal, setShowModal] = useState(false); // Control modal visibility
  const [showUsers, setShowUsers] = useState(false); // Toggle display of Users
  const [showTopics, setShowTopics] = useState(false); // Toggle display of Topics
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const itemsPerPage = 5; // Number of assessments to show per page

  const [sortOption, setSortOption] = useState({
    field: "due_date",
    order: "desc",
  });
  const navigate = useNavigate();

  // Fetch data from API
  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/manager/get_manager_assessments/${manager_id}`,
        );
        // Get the current date and time
        const now = new Date();
        const userTimeZone = moment.tz.guess();
        // Adjust the due_by based on timezone difference
        const expiredAssessments = response.data.assessments
          .map((assessment) => {
            // If the received timezone (tz) is different from the user's timezone
            if (assessment.tz !== "NULL" && assessment.tz !== userTimeZone) {
              // Convert the due_by field to the user's timezone
              const convertedDueBy = moment
                .tz(assessment.due_by, assessment.tz)
                .clone()
                .tz(userTimeZone)
                .format("YYYY-MM-DD HH:mm:ss"); // Format to match your display format
              return {
                ...assessment,
                due_by: convertedDueBy, // Replace the due_by with the converted time
              };
            }
            return assessment;
          })
          .filter((assessment) => new Date(assessment.due_by) < now);

        setAssessmentsData(expiredAssessments);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching assessments data:", err);
        setError("Failed to load data");
        setLoading(false);
      }
    };

    fetchAssessments();
  }, [manager_id]);

  // Handle opening the modal with assessment details
  const handleViewDetails = (assessment) => {
    setSelectedAssessment(assessment);
    setShowModal(true);
    setShowUsers(true); // Reset users/topics display when opening a new modal
    setShowTopics(false);
  };

  // Handle closing the modal
  const closeModal = () => {
    setSelectedAssessment(null);
    setShowModal(false);
  };

  // Handle assessment recommendation navigation
  const handleAssessmentRecommendation = (assessment_id, user_id) => {
    
    
    navigate(`/manager/assessment-quiz-result/${user_id}/${assessment_id}/`);
  };

  // Handle modifying an assessment
  const handleModifyAssessment = (assessment) => {
    navigate(
      `/manager/modify-assessment/${manager_id}/${assessment.assessment_id}`,
    );
  };

  // Handle modifying an assessment
  const handleUserAccess = (assessment) => {
    
    navigate(`/manager/user-access/${manager_id}/${assessment.assessment_id}`);
  };

  // Function to check if any relevant field matches the search query
  const assessmentMatchesSearch = (assessment, query) => {
    if (!query) return true; // If query is empty, include all assessments

    const lowerCaseQuery = query.toLowerCase();

    // Check assessment.name
    if (
      assessment.name &&
      assessment.name.toLowerCase().includes(lowerCaseQuery)
    ) {
      return true;
    }

    // Check assessmentTopics.product_name and assessmentTopics.skill_name
    if (assessment.assessmentTopics && assessment.assessmentTopics.length > 0) {
      for (let topic of assessment.assessmentTopics) {
        if (
          (topic.product_name &&
            topic.product_name.toLowerCase().includes(lowerCaseQuery)) ||
          (topic.skill_name &&
            topic.skill_name.toLowerCase().includes(lowerCaseQuery))
        ) {
          return true;
        }
      }
    }

    // // Check users.name
    // if (assessment.users && assessment.users.length > 0) {
    //   for (let user of assessment.users) {
    //     if (user.name && user.name.toLowerCase().includes(lowerCaseQuery)) {
    //       return true;
    //     }
    //   }
    // }

    // If none of the fields match
    return false;
  };

  // Filter assessments based on search query across specified fields
  const filteredAssessments = assessmentsData
    .filter((assessment) =>
      assessmentMatchesSearch(assessment, searchQuery.trim()),
    )
    .reverse();

  const handleSortOptionClick = (field) => {
    if (sortOption.field === field) {
      setSortOption({
        field,
        order: sortOption.order === "desc" ? "asc" : "desc",
      });
    } else {
      setSortOption({ field, order: "desc" });
    }
  };

  const sortedFilteredAssessments = filteredAssessments.slice().sort((a, b) => {
    const dateA = new Date(a[sortOption.field]);
    const dateB = new Date(b[sortOption.field]);

    // If sorting by creation_date, also consider creation_time
    if (sortOption.field === "creation_date") {
      const timeA = new Date(`${a.creation_date}T${a.creation_time}`);
      const timeB = new Date(`${b.creation_date}T${b.creation_time}`);
      return sortOption.order === "asc"
        ? timeA - timeB || dateA - dateB // Sort by time first, then by date
        : timeB - timeA || dateB - dateA; // Sort by time first, then by date
    }

    // Default sorting for other fields
    return sortOption.order === "asc" ? dateA - dateB : dateB - dateA;
  });

  const dropContent = (
    <Box pad="small" gap="small">
      <Button plain onClick={() => handleSortOptionClick("due_by")}>
        <Box direction="row" align="center" gap="xsmall">
          <Text>Due Date</Text>
          {/* Show an icon for the currently active sort field */}
          {sortOption.field === "due_by" &&
            (sortOption.order === "asc" ? <FormUp /> : <FormDown />)}
        </Box>
      </Button>
      <Button plain onClick={() => handleSortOptionClick("creation_date")}>
        <Box direction="row" align="center" gap="xsmall">
          <Text>Creation Date</Text>
          {sortOption.field === "creation_date" &&
            (sortOption.order === "asc" ? <FormUp /> : <FormDown />)}
        </Box>
      </Button>
    </Box>
  );

  // Pagination: Get current assessments based on currentPage
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAssessments = sortedFilteredAssessments.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  
  // Handle pagination: Previous Page
  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  // Handle pagination: Next Page
  const handleNextPage = () => {
    setCurrentPage((prev) =>
      prev * itemsPerPage >= sortedFilteredAssessments.length ? prev : prev + 1,
    );
  };

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Loading state UI
  if (loading) {
    return (
      <Box fill align="center" justify="center" pad="medium">
        {/* Simple Loader */}
        <Box align="center" gap="small">
          <Spinner size="medium" color="brand" />
          <Text size="medium" weight="bold">
            Loading...
          </Text>
        </Box>
      </Box>
    );
  }

  // Error state UI
  if (error) {
    return (
      <Box pad="medium" align="center">
        <Text color="status-critical">{error}</Text>
      </Box>
    );
  }

  // Loading state UI
  if (loading) {
    return (
      <Box fill align="center" justify="center" pad="medium">
        <Box align="center" gap="small">
          <Spinner size="medium" color="brand" />
          <Text size="medium" weight="bold">
            Loading...
          </Text>
        </Box>
      </Box>
    );
  }

  // Error state UI
  if (error) {
    return (
      <Box pad="medium" align="center">
        <Text color="status-critical">{error}</Text>
      </Box>
    );
  }

  return (
    <Box fill="vertical" justify="between">
      {/* Main content container */}
      <Box flex="grow">
        <HeaderNavigation />
        <BreadcrumbHeader
          title1="Manage Assessments"
          title2="Archived Assessments"
        />
        <Box align="center" margin={{ vertical: "medium" }}>
          <Heading level={2} margin={{ vertical: "medium" }}>
            Archived Assessments
          </Heading>
        </Box>

        {/* Search Bar and Refresh Button */}
        {/* <Box
          direction="row"
          justify="between"
          align="center"
          margin={{ bottom: "medium" }}
          pad="medium"
        >
          <Box width="large">
            <TextInput
              placeholder="Search assessments by assessment name or skills"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              plain={false}
              width={{ max: "large" }}
              icon={<View />}
            />
          </Box>
          <Box>
            <Button
              icon={<Refresh />}
              onClick={() => window.location.reload()}
              title="Refresh Page"
              plain={false}
              color="brand"
              size="small"
              hoverIndicator
            />
          </Box>
        </Box> */}

        <Box
          direction="row"
          gap="small"
          margin={{ horizontal: "medium" }}
          justify="between"
          flex="grow"
        >
          {/* Left: Search Input and Sort Options */}
          <Box direction="row" gap="small" align="center" justify="between">
            <Box width="large">
              <TextInput
                placeholder="Search assessments by assessment name or skills"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                icon={<View />}
              />
            </Box>
            <Box direction="row" align="center" alignContent="center">
              <Text weight="bold">Sort by: </Text>
              <DropButton
                dropContent={dropContent}
                dropAlign={{ top: "bottom", left: "left" }}
                label={
                  <>
                    <Text>
                      {sortOption.field === "creation_date"
                        ? "Creation Date"
                        : "Due Date"}
                    </Text>
                    {sortOption.order === "asc" ? <FormUp /> : <FormDown />}
                  </>
                }
              />
            </Box>
          </Box>

          {/* Right: Refresh Button */}
          <Button
            icon={<Refresh />}
            onClick={() => window.location.reload()}
            title="Refresh Data"
            color="brand"
            size="small"
            hoverIndicator
          />
        </Box>

        {/* Render AssessmentList Component */}
        <Box pad="medium">
          <AssessmentList
            currentAssessments={currentAssessments}
            sortedFilteredAssessments={sortedFilteredAssessments}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            indexOfLastItem={indexOfLastItem}
            showModal={showModal}
            selectedAssessment={selectedAssessment}
            showUsers={showUsers}
            showTopics={showTopics}
            setShowUsers={setShowUsers}
            setShowTopics={setShowTopics}
            closeModal={closeModal}
            handlePreviousPage={handlePreviousPage}
            handleNextPage={handleNextPage}
            handleViewDetails={handleViewDetails}
            handleModifyAssessment={handleModifyAssessment}
            handleUserAccess={handleUserAccess}
            handleAssessmentRecommendation={handleAssessmentRecommendation}
            navigate={navigate}
            requiredButton={false}
            archive={false}
          />
        </Box>
      </Box>
      <StickyFooter />
    </Box>
  );
};

export default ManagerAssessmentArchive;
